import React, { useState, useContext } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, TextField, Button, Divider, Tooltip,
  Select, MenuItem, InputLabel, FormControl,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { AuthContext } from '../../App';
import { selectVehicles, createVehicle, updateVehicle, deleteVehicle } from '../../store/slices/vehiclesSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import { t } from '../../i18n';

const VEHICLE_TYPES = [
  { value: 'cisterna', label: 'Camião Cisterna', labelEn: 'Tanker Truck', icon: '🛢' },
  { value: 'frigorifico', label: 'Camião Frigorífico', labelEn: 'Reefer Truck', icon: '❄' },
  { value: 'pesado', label: 'Camião Pesado', labelEn: 'Heavy Truck', icon: '🚛' },
  { value: 'ligeiro', label: 'Camião Ligeiro', labelEn: 'Light Truck', icon: '🚚' },
  { value: 'carro', label: 'Carro', labelEn: 'Car', icon: '🚗' },
];

const FUEL_TYPES = [
  { value: 'diesel', label: 'Gasóleo', labelEn: 'Diesel' },
  { value: 'gasoline', label: 'Gasolina', labelEn: 'Gasoline' },
  { value: 'gnl', label: 'GNL', labelEn: 'LNG' },
  { value: 'electric', label: 'Elétrico', labelEn: 'Electric' },
];

const DEFAULTS = {
  cisterna:    { weight: '40', height: '4.0', width: '2.55', length: '18.75', axleLoad: '11.5', fuelPer100km: '35' },
  frigorifico: { weight: '26', height: '4.0', width: '2.55', length: '13.6',  axleLoad: '10',   fuelPer100km: '30' },
  pesado:      { weight: '26', height: '4.0', width: '2.55', length: '13.6',  axleLoad: '10',   fuelPer100km: '28' },
  ligeiro:     { weight: '7.5', height: '3.0', width: '2.2',  length: '7.5',   axleLoad: '5',    fuelPer100km: '18' },
  carro:       { weight: '',    height: '',     width: '',      length: '',       axleLoad: '',     fuelPer100km: '8' },
};

const emptyForm = {
  plate: '', vehicleType: 'pesado', fuelType: 'diesel', capacity: '',
  fuelPer100km: '28', weight: '26', height: '4.0', width: '2.55', length: '13.6', axleLoad: '10',
  driverId: '',
};

const useStyles = makeStyles(() => ({
  header: {
    background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
    padding: '16px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: '1px solid #bfdbfe',
  },
  headerTitle: { color: '#1e40af', fontWeight: 700, fontSize: '0.95rem' },
  headerSub: { color: '#64748b', fontSize: '0.7rem' },
  content: { padding: '16px 20px 20px', background: '#f8fafc' },
  item: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    borderRadius: 8, marginBottom: 6,
    border: '1px solid #e2e8f0', background: '#ffffff',
    transition: 'all 0.15s',
    '&:hover': { background: '#f1f5f9', borderColor: '#cbd5e1' },
  },
  itemIcon: {
    width: 36, height: 36, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f1f5f9', fontSize: '1.1rem', flexShrink: 0,
  },
  itemPlate: { fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' },
  itemType: { fontSize: '0.68rem', color: '#64748b' },
  itemDriver: { fontSize: '0.65rem', color: '#3b82f6', fontWeight: 500 },
  itemMeta: { fontSize: '0.62rem', color: '#94a3b8' },
  addForm: {
    marginTop: 12, padding: 16, background: '#ffffff', borderRadius: 12,
    border: '1px solid #e2e8f0',
  },
  formSection: {
    fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6, marginTop: 12,
  },
  formRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' },
  input: {
    '& .MuiInputBase-root': { fontSize: '0.78rem' },
    '& .MuiOutlinedInput-input': { padding: '8px 10px' },
    '& .MuiInputLabel-root': { fontSize: '0.72rem' },
  },
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 5, marginBottom: 8 },
  typeCard: {
    textAlign: 'center', padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
    transition: 'all 0.2s', border: '1.5px solid #e2e8f0', background: '#ffffff',
  },
  typeCardActive: {
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    color: '#fff', borderColor: '#3b82f6',
    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
  },
  empty: { textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem', padding: '24px 0' },
}));

function ManageVehiclesDialog({ open, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const vehicles = useSelector(selectVehicles);
  const drivers = useSelector(selectDrivers);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  function resetAdd() { setForm({ ...emptyForm }); setShowAdd(false); setError(''); }

  function handleTypeSelect(vehicleType) {
    const d = DEFAULTS[vehicleType] || {};
    setForm(f => ({ ...f, vehicleType, ...d }));
  }

  async function handleAdd() {
    if (!form.plate.trim()) return setError('Matrícula obrigatória');
    try {
      const vt = VEHICLE_TYPES.find(v => v.value === form.vehicleType);
      await dispatch(createVehicle({
        token,
        data: {
          plate: form.plate.trim(),
          type: vt ? vt.label : form.vehicleType,
          capacity: form.capacity.trim() || null,
          fuelPer100km: form.fuelPer100km ? Number(form.fuelPer100km) : null,
          fuelType: form.fuelType || 'diesel',
          weight: form.weight || null,
          height: form.height || null,
          width: form.width || null,
          length: form.length || null,
          axleLoad: form.axleLoad || null,
          driverId: form.driverId || null,
        },
      })).unwrap();
      resetAdd();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUpdate(id) {
    try {
      const vt = VEHICLE_TYPES.find(v => v.value === form.vehicleType);
      await dispatch(updateVehicle({
        token, id,
        data: {
          plate: form.plate,
          type: vt ? vt.label : form.vehicleType,
          capacity: form.capacity || null,
          fuelPer100km: form.fuelPer100km ? Number(form.fuelPer100km) : null,
          fuelType: form.fuelType || 'diesel',
          weight: form.weight || null,
          height: form.height || null,
          width: form.width || null,
          length: form.length || null,
          axleLoad: form.axleLoad || null,
          driverId: form.driverId || null,
        },
      })).unwrap();
      setEditId(null); setShowAdd(false);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    try { await dispatch(deleteVehicle({ token, id })).unwrap(); } catch (err) { setError(err.message); }
  }

  function startEdit(v) {
    const vt = VEHICLE_TYPES.find(t => t.label === v.type || t.value === v.type);
    setEditId(v.id);
    setForm({
      plate: v.plate || '', vehicleType: vt ? vt.value : 'pesado',
      fuelType: v.fuelType || 'diesel', capacity: v.capacity || '',
      fuelPer100km: v.fuelPer100km || '', weight: v.weight || '',
      height: v.height || '', width: v.width || '', length: v.length || '',
      axleLoad: v.axleLoad || '', driverId: v.driverId || '',
    });
    setShowAdd(true);
  }

  const lang = 'pt'; // from context would be better but keeping simple

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.headerTitle}>
            <LocalShippingIcon style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
            {t(lang, 'manageVehicles')}
          </Typography>
          <Typography className={classes.headerSub}>{vehicles.length} veículo(s)</Typography>
        </Box>
        <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
          style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px', background: '#fff' }}>
          Fechar
        </Button>
      </Box>
      <DialogContent className={classes.content} style={{ maxHeight: 520, overflowY: 'auto' }}>
        {vehicles.length === 0 && !showAdd && (
          <Typography className={classes.empty}>Nenhum veículo registado</Typography>
        )}

        {!showAdd && vehicles.map(v => {
          const vt = VEHICLE_TYPES.find(t => t.label === v.type) || {};
          const driver = v.driverId ? drivers.find(d => String(d.id) === String(v.driverId)) : null;
          return (
            <Box key={v.id} className={classes.item}>
              <Box className={classes.itemIcon}>{vt.icon || '🚛'}</Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.itemPlate}>{v.plate}</Typography>
                <Typography className={classes.itemType}>{v.type || '—'}</Typography>
                {driver && <Typography className={classes.itemDriver}>{driver.name}</Typography>}
              </Box>
              <Box style={{ textAlign: 'right' }}>
                <Typography className={classes.itemMeta}>
                  {v.fuelPer100km ? `${v.fuelPer100km} L/100km` : ''}
                </Typography>
                {v.weight && <Typography className={classes.itemMeta}>{v.weight}t</Typography>}
              </Box>
              <Tooltip title="Editar"><IconButton size="small" onClick={() => startEdit(v)} style={{ color: '#64748b' }}>
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton></Tooltip>
              <Tooltip title="Eliminar"><IconButton size="small" onClick={() => handleDelete(v.id)} style={{ color: '#ef4444' }}>
                <DeleteIcon style={{ fontSize: 14 }} />
              </IconButton></Tooltip>
            </Box>
          );
        })}

        {error && (
          <Typography style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 8, textAlign: 'center' }}>{error}</Typography>
        )}

        {showAdd ? (
          <Box className={classes.addForm}>
            <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
              {editId ? 'Editar Veículo' : 'Novo Veículo'}
            </Typography>

            {/* Vehicle Type icons */}
            <Typography className={classes.formSection} style={{ marginTop: 0 }}>{t(lang, 'vehicleTypeLbl')}</Typography>
            <Box className={classes.typeGrid}>
              {VEHICLE_TYPES.map(vt => (
                <Box key={vt.value}
                  onClick={() => handleTypeSelect(vt.value)}
                  className={`${classes.typeCard} ${form.vehicleType === vt.value ? classes.typeCardActive : ''}`}
                >
                  <div style={{ fontSize: '1.2rem', marginBottom: 2 }}>{vt.icon}</div>
                  <div style={{ fontSize: '0.58rem', fontWeight: 700, lineHeight: 1.2 }}>{vt.label}</div>
                </Box>
              ))}
            </Box>

            {/* Plate + Capacity */}
            <Typography className={classes.formSection}>{t(lang, 'plate')} / {t(lang, 'capacity')}</Typography>
            <Box className={classes.formRow}>
              <TextField className={classes.input} variant="outlined" size="small" label={t(lang, 'plate')} required
                value={form.plate} onChange={e => { setForm(f => ({ ...f, plate: e.target.value })); setError(''); }} style={{ flex: 1 }} />
              <TextField className={classes.input} variant="outlined" size="small" label={t(lang, 'capacity')}
                value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} style={{ flex: 1 }} />
            </Box>

            {/* Fuel */}
            <Typography className={classes.formSection}>{t(lang, 'fuel')}</Typography>
            <Box className={classes.formRow}>
              <Box style={{ display: 'flex', gap: 4, flex: 1 }}>
                {FUEL_TYPES.map(ft => (
                  <Box key={ft.value}
                    onClick={() => setForm(f => ({ ...f, fuelType: ft.value }))}
                    style={{
                      flex: 1, textAlign: 'center', padding: '5px 2px', borderRadius: 6, cursor: 'pointer',
                      fontSize: '0.68rem', fontWeight: 600,
                      background: form.fuelType === ft.value ? '#3b82f6' : '#f1f5f9',
                      color: form.fuelType === ft.value ? '#fff' : '#64748b',
                      border: `1px solid ${form.fuelType === ft.value ? '#3b82f6' : '#e2e8f0'}`,
                    }}
                  >{ft.label}</Box>
                ))}
              </Box>
              <TextField className={classes.input} variant="outlined" size="small" label="L/100km" type="number"
                value={form.fuelPer100km} onChange={e => setForm(f => ({ ...f, fuelPer100km: e.target.value }))} style={{ width: 90 }} />
            </Box>

            {/* Dimensions (only for trucks) */}
            {form.vehicleType !== 'carro' && (<>
              <Typography className={classes.formSection}>{t(lang, 'truckDimensions')}</Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
                {[
                  { key: 'weight', label: 'Peso (t)', ph: '26' },
                  { key: 'axleLoad', label: 'Eixo (t)', ph: '10' },
                  { key: 'height', label: 'Altura (m)', ph: '4.0' },
                  { key: 'width', label: 'Largura (m)', ph: '2.55' },
                  { key: 'length', label: 'Comprim. (m)', ph: '13.6' },
                ].map(({ key, label, ph }) => (
                  <TextField key={key} className={classes.input} variant="outlined" size="small"
                    label={label} placeholder={ph} type="number"
                    value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  />
                ))}
              </Box>
            </>)}

            {/* Driver association */}
            <Typography className={classes.formSection}>{t(lang, 'driver')}</Typography>
            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
              <Select
                value={form.driverId}
                onChange={e => setForm(f => ({ ...f, driverId: e.target.value }))}
                displayEmpty
                style={{ fontSize: '0.82rem' }}
              >
                <MenuItem value="" style={{ fontSize: '0.82rem' }}>
                  <em>— {lang === 'pt' ? 'Sem motorista associado' : 'No driver assigned'} —</em>
                </MenuItem>
                {drivers.map(d => (
                  <MenuItem key={d.id} value={d.id} style={{ fontSize: '0.82rem' }}>
                    {d.name}{d.phone ? ` (${d.phone})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => { resetAdd(); setEditId(null); }}
                style={{ fontSize: '0.75rem', color: '#64748b' }}>{t(lang, 'cancel')}</Button>
              <Button size="small" variant="contained" onClick={() => editId ? handleUpdate(editId) : handleAdd()}
                style={{ fontSize: '0.75rem', background: '#3b82f6', color: '#fff', borderRadius: 8, textTransform: 'none' }}>
                {editId ? t(lang, 'save') : 'Adicionar'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box style={{ marginTop: 12 }}>
            <Divider style={{ marginBottom: 12 }} />
            <Button size="small" startIcon={<AddIcon style={{ fontSize: 16 }} />} onClick={() => { setEditId(null); setForm({ ...emptyForm }); setShowAdd(true); }}
              style={{ fontSize: '0.78rem', color: '#3b82f6', textTransform: 'none', fontWeight: 600 }}>
              Adicionar Veículo
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ManageVehiclesDialog;
